import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar, SpinLoading, SearchBar, Skeleton, Button, Popup, List, SearchBarRef, Toast, Modal } from 'antd-mobile';
import { useQuery } from '@apollo/client';
import { getNavBarContainerStyle } from '../../../../utils/deviceUtils';
import { t } from '../../../../i18n';
import { DspPath } from '../../../../enums/DeliveryNavigatePathEnum';
import { GET_DSP_SCAN_INFO_DETAIL, GetDspScanInfoDetailResponse, DspScanInfoDetailType } from '../../graphql/queries/getDspScanInfoDetail';
import { getExpandableText } from '../../../../utils/textUtils';
import { handleSubmitDspReceivingSortingReview } from '../../graphql/mutations/submitDspReceivingSortingReview';
import { DspHandleBatchNewAssignmentOutForDeliveryEvent } from '../../graphql/mutations/dspHandleBatchNewAssignmentOutForDeliveryEvent';
import { GET_AVAILABLE_DRIVERS, GetAvailableDriversResponse, DriverType } from '../../graphql/queries/getAvailableDrivers';
import { GET_ACCOUNT_BIND_DSP, GetAccountBindDspResponse } from '../../graphql/queries/getAccountBindDsp';
import { useApolloClient } from '@apollo/client';
import { useRef } from 'react';
import { getUserInfo } from '../../../../utils/auth';
import useDspWarehouseStore from '../../store/dspWarehouseStore';

/**
 * Receiving Sorting - 扫描汇总页面
 */
const ReceivingSortingSummaryPage: React.FC = () => {
    const navigate = useNavigate();
    const selectedWarehouseId = useDspWarehouseStore((state) => state.selectedWarehouseId);
    const warehouseIdVar = selectedWarehouseId ? parseInt(selectedWarehouseId) : undefined;

    // 获取DSP信息
    const {
        data: dspData,
        loading: dspLoading,
        error: dspError
    } = useQuery<GetAccountBindDspResponse>(GET_ACCOUNT_BIND_DSP);

    // 获取运单数据
    const {
        data: orderData,
        loading: orderLoading,
        error: orderError,
        refetch
    } = useQuery<GetDspScanInfoDetailResponse>(
        GET_DSP_SCAN_INFO_DETAIL,
        {
            variables: { warehouseId: warehouseIdVar },
            fetchPolicy: 'network-only'
        }
    );


    const dspOrderData = orderData?.getDspScanInfoDetail;
    const dspId = dspData?.dsp?.id ? parseInt(dspData.dsp.id) : null;

    // 统计数据：线路 & 单量 & 包裹扫描情况
    const {
        scannedRouteCount,
        expectedParcels,
        scannedParcelCount,
        missedParcelList,
        dspScanDeliveryTaskOrderIdList,
        totalRouteCount
    } = useMemo(() => {
        const parcels = dspOrderData?.parcelList || [];
        
        return {
            scannedRouteCount: dspOrderData?.scannedRouteCount ?? 0,
            expectedParcels: dspOrderData?.expectedParcels ?? 0,
            scannedParcelCount: dspOrderData?.successScanParcelCount ?? 0,
            missedParcelList: parcels,
            dspScanDeliveryTaskOrderIdList: dspOrderData?.dspScanDeliveryTaskOrderIdList || [],
            totalRouteCount: dspOrderData?.totalRouteCount ?? 0
        };
    }, [dspOrderData]);

    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const [confirmModalVisible, setConfirmModalVisible] = useState<boolean>(false);
    const [parcelsToSubmit, setParcelsToSubmit] = useState<DspScanInfoDetailType[]>([]);

    const handleBack = () => {
        navigate(DspPath.DELIVERY_DSP_RECEIVING_SORTING_SCAN);
    };

    const toggleRowExpanded = (trackingNumber: string) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            if (next.has(trackingNumber)) {
                next.delete(trackingNumber);
            } else {
                next.add(trackingNumber);
            }
            return next;
        });
    };


    const handleConfirmSubmit = async () => {
        try {
            let submitParcels: DspScanInfoDetailType[] = [];
            // 如果选择"全部"，提交所有漏扫包裹，但保证每个司机ID只有一条数据
            const driverIdMap = new Map<number, DspScanInfoDetailType>();
            missedParcelList.forEach(parcel => {
                if (parcel.assignedDriverId && !driverIdMap.has(parcel.assignedDriverId)) {
                    driverIdMap.set(parcel.assignedDriverId, parcel);
                }
            });
            submitParcels = Array.from(driverIdMap.values());
            // 检查 submitParcels 是否为空
            if (submitParcels.length === 0) {
                Toast.show({
                    content: t('pages.dsp.home.receivingSorting.summaryPage.driverNoScan'),
                    position: 'bottom',
                });
                return;
            }
            
            // 保存待提交的包裹列表
            setParcelsToSubmit(submitParcels);
            // 显示确认弹框
            setConfirmModalVisible(true);
        } catch (error) {
            console.error('提交审核失败:', error);
        }
    };

    const handleConfirmReview = async () => {
        try {
            // 关闭确认弹框
            setConfirmModalVisible(false);
            
            // 获取当前登录用户的名字
            const currentUser = getUserInfo();
            
            // 调用提交接口
            const result = await handleSubmitDspReceivingSortingReview(parcelsToSubmit, currentUser?.account?.name || null);
            if (result) {
                // 显示 Toast 提示
                Toast.show({
                    content: `${t('pages.dsp.home.receivingSorting.summaryPage.submissionDesc1')}`,
                    position: 'bottom'
                });
                navigate(DspPath.DELIVERY_DSP_RECEIVING_SORTING_SCAN);
            }
        } catch (error) {
            console.error('提交审核失败:', error);
        }
    };


    const handleStartDelivery = async () => {
        // 检查线路应取包裹量是否为0
        if (expectedParcels === 0) {
            Toast.show({
                content: t('pages.dsp.home.receivingSorting.summaryPage.noScan'),
                position: 'bottom',
            });
            return;
        }
        
        try {
            // 从parcelList中提取司机ID列表并去重
            const driverIdList = dspOrderData?.driverIdList || []

            // 检查DSP ID是否存在
            if (!dspId) {
                Toast.show({
                    content: 'DSP ID is missing',
                    position: 'bottom',
                });
                return;
            }

            // 获取当前登录用户的名字
            const currentUser = getUserInfo();
            
            // 调用批量发车事件接口
            const result = await DspHandleBatchNewAssignmentOutForDeliveryEvent(dspScanDeliveryTaskOrderIdList, driverIdList, dspId, currentUser?.account?.name || null);
            if (result) {
                // 显示成功提示
                Toast.show({
                    content: t('pages.dsp.home.receivingSorting.summaryPage.startDeparture'),
                    position: 'bottom',
                });
                // 跳转到扫描页面
                navigate(DspPath.DELIVERY_DSP_RECEIVING_SORTING_SCAN);
            }
        } catch (error) {
            console.error('批量发车事件处理失败:', error);
        }
    };

    return (
        <div className="flex flex-col h-screen bg-gray-50">
            {/* Navbar */}
            <div {...getNavBarContainerStyle()} className="bg-white">
                <NavBar onBack={handleBack}>
                    {!orderLoading && (
                        <span className="font-bold">
                            {scannedParcelCount === expectedParcels ? t('pages.dsp.home.receivingSorting.summaryPage.title2') : t('pages.dsp.home.receivingSorting.summaryPage.title1')}
                        </span>
                    )}
                </NavBar>
            </div>

            {/* 统计信息 */}
            {!orderLoading && (
                <div className="bg-white px-4 py-3">
                    <div className="text-sm text-gray-600 font-bold mb-2">
                        {t('pages.dsp.home.receivingSorting.summaryPage.dspScanRoute')}
                    </div>
                    <div className="flex gap-4">
                        <div className="flex-1 bg-purple-50 rounded-lg p-4 text-center">
                            <div className="text-[10px] leading-tight text-black mb-1 font-bold truncate">{t('pages.dsp.home.receivingSorting.summaryPage.alreadyScanRoute')}</div>
                            <div className="text-2xl font-semibold text-gray-600">{totalRouteCount}</div>
                        </div>
                        <div className="flex-1 bg-gray-50 rounded-lg p-4 text-center">
                            <div className="text-[10px] leading-tight text-black mb-1 font-bold truncate">{t('pages.dsp.home.receivingSorting.summaryPage.shouldGetPackage')}</div>
                            <div className="text-2xl font-semibold text-gray-600">{expectedParcels}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* 待扫描包裹明细 */}
            {!orderLoading && (
                <div className="bg-white px-4 py-3 mt-2 border-b border-gray-200">
                    <div className="text-left py-1">
                        <div className="text-base text-gray-600">
                            {t('pages.dsp.home.receivingSorting.summaryPage.dspScanTotalRoute')}: <span className="font-bold">{scannedRouteCount}</span>
                        </div>
                    </div>
                    <div className="text-left py-1">
                        <div className="text-base text-gray-600">
                            {t('pages.dsp.home.receivingSorting.summaryPage.dspScanTotalPackage')}: <span className="font-bold">{scannedParcelCount}</span>
                        </div>
                    </div>
                </div>
            )}

            {/* 表头 */}
            {!orderLoading && (
                <div className="bg-white px-4 py-3 border-b border-gray-200">
                    <div className="text-left py-1">
                        <div className="text-base text-red-500 font-medium">
                            {t('pages.dsp.home.receivingSorting.summaryPage.dspWaitScanCount',{waitScanCount: expectedParcels - scannedParcelCount})}
                        </div>
                    </div>
                    <div className="grid grid-cols-12 py-3 gap-1 font-medium text-gray-500">
                        <div className="col-span-6 px-1 text-sm text-center">
                            {t('pages.dsp.home.receivingSorting.summaryPage.trackingNumber')}
                        </div>
                        <div className="col-span-2 px-1 text-sm text-center">
                            {t('pages.dsp.home.receivingSorting.summaryPage.area')}
                        </div>
                        <div className="col-span-4 px-1 text-sm text-center">
                            {t('pages.dsp.home.receivingSorting.summaryPage.assignedDriver')}
                        </div>
                    </div>
                </div>
            )}

            {/* 漏扫包裹列表 */}
            <div className="flex-1 overflow-y-auto bg-white">
                {(orderLoading) ? (
                    <div className="p-4">
                        {Array.from({length: 8}).map((_, index) => (
                            <div key={index} className="grid grid-cols-12 py-3 border-b border-gray-100">
                                <div className="col-span-5 px-1 flex items-center justify-center">
                                    <Skeleton.Title style={{width: '90%'}} animated/>
                                </div>
                                <div className="col-span-3 px-1 flex items-center justify-center">
                                    <Skeleton.Title style={{width: '60%'}} animated/>
                                </div>
                                <div className="col-span-4 px-1 flex items-center justify-center">
                                    <Skeleton.Title style={{width: '70%'}} animated/>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : missedParcelList.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-gray-400 text-sm">
                            {t('pages.dsp.home.receivingSorting.summaryPage.noData')}
                        </div>
                    </div>
                ) : (
                    missedParcelList.map((parcel, index) => {
                        const driverName = parcel.assignedDriver || '-';
                        const isExpanded = expandedRows.has(parcel.trackingNumber);
                        const { displayText, needsTruncate } = getExpandableText(driverName, isExpanded, 7);

                        return (
                            <div
                                key={`${parcel.trackingNumber}-${index}`}
                                className="grid grid-cols-12 py-3 border-b border-gray-100 gap-1"
                            >
                                <div className="col-span-6 px-1 font-mono text-sm text-center whitespace-nowrap overflow-hidden">
                                    {parcel.trackingNumber}
                                </div>
                                <div className="col-span-2 px-1 font-mono text-xs text-center whitespace-nowrap overflow-hidden">
                                    {parcel.areaName}
                                </div>
                                <div
                                    className={`col-span-4 px-1 font-mono text-sm text-center break-words ${
                                        needsTruncate ? 'cursor-pointer active:bg-gray-100' : ''
                                    }`}
                                    onClick={() =>
                                        needsTruncate && toggleRowExpanded(parcel.trackingNumber)
                                    }
                                >
                                    {displayText}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* 底部按钮 */}
            {!orderLoading && (
                <div className="p-4 bg-white border-t border-gray-200">
                    {scannedParcelCount === expectedParcels ? (
                        <Button
                            color="primary"
                            block
                            onClick={handleStartDelivery}
                        >
                            {t('pages.dsp.home.receivingSorting.summaryPage.OutForDelivery')}
                        </Button>
                    ) : (
                        <div className="flex gap-2">
                            <Button
                                color="default"
                                className="flex-1"
                                onClick={handleConfirmSubmit}
                            >
                                {t('pages.dsp.home.receivingSorting.summaryPage.submitReview')}
                            </Button>
                            <Button
                                color="primary"
                                className="flex-1"
                                onClick={() => navigate(DspPath.DELIVERY_DSP_RECEIVING_SORTING_SCAN)}
                            >
                                {t('pages.dsp.home.receivingSorting.summaryPage.continueScanning')}
                            </Button>
                        </div>
                    )}
                </div>
            )}
            
            {/* 确认弹框 */}
            <Modal
                visible={confirmModalVisible}
                content={
                    <div style={{
                        minWidth: '280px',
                        maxWidth: '90vw',
                        width: '100%', // 确保宽度自适应
                        padding: '2rem 1.5rem',
                        boxSizing: 'border-box',
                        borderRadius: '8px',
                        backgroundColor: '#ffffff',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center', // 整体内容水平居中
                    }}>
                        {/* 标题区域 - 置顶居中 */}
                        <div style={{
                            width: '100%',
                            textAlign: 'center',
                            marginBottom: '1.5rem', // 与文案保持合适间距
                        }}>
        <span style={{
            fontSize: '1.125rem', // 等价于 text-lg
            fontWeight: 'bold',
            color: '#1f2937', // 等价于 text-gray-800
        }}>
          {t('pages.dsp.home.receivingSorting.summaryPage.title3')}
        </span>
                        </div>

                        {/* 文案区域 - 居中左对齐 */}
                        <div style={{
                            width: '100%',
                            maxWidth: '600px', // 限制文案最大宽度，避免太宽影响阅读
                            marginBottom: '2rem', // 与按钮保持合适间距
                            padding: '0 0.5rem', // 左右留白
                        }}>
                            <span style={{
                                fontSize: '0.8rem',
                                color: '#1f2937', // 等价于 text-gray-800
                                display: 'block', // 确保左对齐生效
                                textAlign: 'left',
                                lineHeight: '1.5', // 增加行高提升可读性
                            }}>
                                {t('pages.dsp.home.receivingSorting.summaryPage.submissionDesc2')}
                            </span>
                        </div>

                        {/* 按钮区域 - 均匀对称分布 */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between', // 两端对齐实现对称
                            width: '100%',
                            maxWidth: '240px', // 限制按钮容器最大宽度，保证按钮间距合理
                            boxSizing: 'border-box',
                            marginTop: 'auto', // 确保按钮在弹窗底部（可选，根据需求调整）
                        }}>
                            {/* 取消按钮 */}
                            <Button
                                color="default"
                                onClick={() => setConfirmModalVisible(false)}
                                style={{
                                    width: '100px',
                                    padding: '0.65rem 0',
                                    textAlign: 'center',
                                    borderRadius: '6px',
                                    border: '1px solid #e5e7eb',
                                    backgroundColor: '#f9fafb',
                                    color: '#374151',
                                    cursor: 'pointer',
                                    boxSizing: 'border-box',
                                    flex: '0 0 100px', // 固定宽度，防止伸缩
                                }}
                            >
                                {t('pages.dsp.home.receivingSorting.summaryPage.cancel')}
                            </Button>

                            {/* 确认按钮 */}
                            <Button
                                color="primary"
                                onClick={handleConfirmReview}
                                style={{
                                    width: '100px',
                                    padding: '0.65rem 0',
                                    textAlign: 'center',
                                    borderRadius: '6px',
                                    border: 'none',
                                    backgroundColor: '#2563eb',
                                    color: '#ffffff',
                                    cursor: 'pointer',
                                    boxSizing: 'border-box',
                                    flex: '0 0 100px', // 固定宽度，防止伸缩
                                }}
                            >
                                {t('pages.dsp.home.receivingSorting.summaryPage.confirm')}
                            </Button>
                        </div>
                    </div>
                }
                closeOnMaskClick={false}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1rem',
                }}
                maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}
            />


        </div>
    );
};

export default ReceivingSortingSummaryPage;